import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Globe, Compass, ArrowUpRight, Rss } from "lucide-react";
import PostListPage from "@/pages/PostListPage";
import { api } from "@/lib/api";
import { SectionKicker, FadeIn } from "@/components/shared/SectionBits";

const SOURCE_TAG = {
    "BBC News": "bg-red-600/10 text-red-700 border-red-200",
    "Sky News": "bg-sky-600/10 text-sky-700 border-sky-200",
    "Financial Times": "bg-amber-700/10 text-amber-800 border-amber-200",
};

function ExternalFeeds() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/news/external")
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <section data-testid="external-feeds-section" className="bg-dr-grey py-24">
            <div className="dr-container">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-10">
                    <div className="md:col-span-8">
                        <SectionKicker>External briefings</SectionKicker>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            className="font-display font-bold text-dr-navy text-4xl md:text-5xl leading-tight"
                        >
                            Security &amp; risk headlines, curated from the BBC, Sky News and the Financial Times.
                        </motion.h2>
                    </div>
                    <div className="md:col-span-4 md:text-right text-xs uppercase tracking-[0.24em] text-dr-dark/60 inline-flex items-center gap-2 md:justify-end">
                        <Rss className="w-3.5 h-3.5" /> Filtered for security &amp; risk · refreshed every 15 minutes
                    </div>
                </div>

                {loading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="h-40 bg-white rounded-2xl border border-dr-silver animate-pulse" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <p className="text-dr-dark/60 py-6">External feeds are temporarily unavailable. Please check back shortly.</p>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {items.slice(0, 12).map((it, i) => (
                            <FadeIn key={`${it.source}-${it.title}-${i}`} delay={i * 0.03}>
                                <a
                                    href={it.link}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    data-testid={`external-item-${i}`}
                                    className="group flex flex-col h-full bg-white border border-dr-silver rounded-2xl p-6 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(8,41,74,0.08)] transition-all duration-500"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border font-medium ${SOURCE_TAG[it.source] || "bg-dr-navy/5 text-dr-navy border-dr-silver"}`}>
                                            <Globe className="w-3 h-3" /> {it.source}
                                        </span>
                                        <ExternalLink className="w-4 h-4 text-dr-dark/40 group-hover:text-dr-navy transition-colors" />
                                    </div>
                                    <h3 className="font-display text-lg font-semibold text-dr-navy leading-snug line-clamp-3">{it.title}</h3>
                                    {it.summary && (
                                        <p className="text-sm text-dr-dark/70 mt-3 leading-relaxed line-clamp-3">{it.summary}</p>
                                    )}
                                    <p className="text-[11px] uppercase tracking-widest text-dr-dark/50 mt-auto pt-4">
                                        {it.category}
                                        {it.published ? ` · ${new Date(it.published).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
                                    </p>
                                </a>
                            </FadeIn>
                        ))}
                    </div>
                )}

                <p className="mt-6 text-xs text-dr-dark/50">
                    Headlines shown are provided by third parties. Dynamic Risks does not author, endorse or
                    verify the content of external publications.
                </p>
            </div>
        </section>
    );
}

function TravelAdvisory() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/news/travel-advisory").then((r) => setData(r.data)).catch(() => setData(null));
    }, []);

    return (
        <section data-testid="travel-advisory-section" className="bg-dr-navy py-24 dr-grain">
            <div className="dr-container">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <p className="text-xs tracking-[0.32em] uppercase text-dr-blue font-medium">Travel advisory</p>
                        <h2 className="font-display font-bold text-white text-4xl md:text-5xl leading-tight mt-4">
                            Official UK Government travel advice, at your fingertips.
                        </h2>
                        <p className="text-white/70 text-lg mt-5 max-w-2xl leading-relaxed">
                            {data?.description || (
                                "Country-by-country travel advice covering entry requirements, safety and security, local laws, health and terrorism risks."
                            )}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                href={data?.url || "https://www.gov.uk/foreign-travel-advice"}
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid="fcdo-primary-link"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-dr-blue text-dr-navy font-semibold hover:bg-white transition-colors"
                            >
                                Check FCDO travel advice
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                            <a
                                href={data?.subscribe_url || "https://www.gov.uk/foreign-travel-advice/email-signup"}
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid="fcdo-subscribe-link"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/40 text-white hover:bg-white hover:text-dr-navy transition-colors"
                            >
                                Subscribe to updates
                            </a>
                        </div>
                        <p className="mt-5 text-xs text-white/40">
                            Provided by the {data?.provider || "Foreign, Commonwealth & Development Office (FCDO)"} — external link, opens in a new tab.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
                            <div className="flex items-center gap-3">
                                <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-dr-blue/15 text-dr-blue">
                                    <Compass className="w-5 h-5" />
                                </span>
                                <p className="font-display text-white font-semibold text-lg">Before you travel</p>
                            </div>
                            <ul className="mt-5 space-y-3 text-white/80 text-sm leading-relaxed">
                                <li className="flex gap-2"><span className="text-dr-blue mt-1">•</span> Check the destination advisory 72 hours before departure.</li>
                                <li className="flex gap-2"><span className="text-dr-blue mt-1">•</span> Confirm entry requirements, vaccinations and visa rules.</li>
                                <li className="flex gap-2"><span className="text-dr-blue mt-1">•</span> Review the terrorism, crime and political risk sections.</li>
                                <li className="flex gap-2"><span className="text-dr-blue mt-1">•</span> Register with the local British consulate for extended stays.</li>
                                <li className="flex gap-2"><span className="text-dr-blue mt-1">•</span> Share your journey plan with a trusted contact.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function NewsPage() {
    return (
        <>
            <PostListPage
                type="news"
                kicker="Newsroom"
                title="News & announcements."
                description="Company news, press releases, awards, events and speaking engagements from Dynamic Risks."
            />
            <ExternalFeeds />
            <TravelAdvisory />
        </>
    );
}
